// Doc 07 §5 (revised) — Template delete confirmation. Deleting a template never
// touches a trip: it only deletes the template document and its own content. Trips
// that already imported it keep their items (including the auto-tag chip, which is
// a name snapshot, not a live lookup) — they just lose sync/refresh/re-import. A
// trip's "Remove template from trip" action (TemplatesForTripModal) is the only way
// to detach the now-dead reference from a specific trip.

import { useState, type ReactNode } from "react";
import { Button, Dialog, vars } from "@pack-it-up/ui-web";

export interface TemplateDeleteDialogProps {
  templateName: string;
  onConfirm: () => Promise<void>;
  onClose: () => void;
}

export const TemplateDeleteDialog = ({ templateName, onConfirm, onClose }: TemplateDeleteDialogProps): ReactNode => {
  const [confirming, setConfirming] = useState(false);

  const handleConfirm = async (): Promise<void> => {
    setConfirming(true);
    try {
      await onConfirm();
    } finally {
      setConfirming(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Delete "${templateName}"`}
      subtitle="This deletes the template and its own content."
      footer={
        <div style={{ display: "flex", gap: vars.space.sm }}>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={confirming}>
            Cancel
          </Button>
          <Button variant="danger" size="sm" onClick={() => { void handleConfirm(); }} disabled={confirming}>
            {confirming ? "Deleting…" : "Delete Template"}
          </Button>
        </div>
      }
    >
      <div style={{ fontSize: vars.font.sizeSm, color: vars.color.textMuted }}>
        Trips that already added this template keep their items — they just won't be
        able to sync, refresh, or re-import from it anymore. To remove its tag from a
        specific trip, use "Remove" in that trip's Templates list.
      </div>
    </Dialog>
  );
};
